import React, { useCallback, useMemo, useRef, useState } from "react";
import { Card, CardBody, CardHeader, CardTitle, Button } from "reactstrap";
import { ClipLoader } from "react-spinners";

import api from "../../../../services/api";
import { useCompanies } from "../../../../hooks/companies";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import { buildTree } from "./buildTree";
import FaturamentoFiltros from "./FaturamentoFiltros";
import FaturamentoVisualExperiments from "./FaturamentoVisualExperiments";
import FaturamentoDocumentList, {
  exportFaturamentoDocuments,
} from "./FaturamentoDocumentList";
import CardPngModal from "./CardPngModal";
import { fmtBRL, fmtInt } from "./formatters";

import "./styles.css";

// PO1-2890: timeout aplicado SÓ nas duas chamadas do indicador, nunca como
// padrão global da instância — um padrão global cortaria telas de exportação
// legitimamente lentas.
//
// Valor alinhado com o limite de consulta do banco (180 s), para não cortar
// uma carga fria legítima antes de o backend desistir. Deve ser reduzido
// depois que a otimização baixar o tempo real medido em produção.
//
// A mensagem exibida ao usuário vem do interceptor de resposta global em
// services/api.js, que traduz o erro cru do axios.
const TIMEOUT_INDICADOR_MS = 180000;

const emptySummary = () => ({
  previstoNf: { quantidade: 0, valor: 0 },
  realizadoCte: { quantidadeCte: 0, quantidade: 0, valor: 0 },
  aRealizarNfsCte: { quantidade: 0, valor: 0 },
  realizadoFatura: { quantidade: 0, quantidadeCte: 0, valor: 0 },
  aRealizarFatura: { quantidade: 0, quantidadeCte: 0, valor: 0 },
});

const getMetric = (row, bucket, field) =>
  Number((row[bucket] && row[bucket][field]) || 0);

const buildSummary = (rows) => {
  const summary = emptySummary();
  const sourceRows = rows.filter((row) => row.nivel === 0);

  sourceRows.forEach((row) => {
    summary.previstoNf.quantidade += getMetric(row, "previstoNf", "quantidade");
    summary.previstoNf.valor += getMetric(row, "previstoNf", "valor");
    summary.realizadoCte.quantidadeCte += getMetric(
      row,
      "realizadoCte",
      "quantidadeCte"
    );
    summary.realizadoCte.quantidade += getMetric(
      row,
      "realizadoCte",
      "quantidade"
    );
    summary.realizadoCte.valor += getMetric(row, "realizadoCte", "valor");
    summary.aRealizarNfsCte.quantidade += getMetric(
      row,
      "aRealizarNfsCte",
      "quantidade"
    );
    summary.aRealizarNfsCte.valor += getMetric(row, "aRealizarNfsCte", "valor");
    summary.realizadoFatura.quantidade += getMetric(
      row,
      "realizadoFatura",
      "quantidade"
    );
    summary.realizadoFatura.quantidadeCte += getMetric(
      row,
      "realizadoFatura",
      "quantidadeCte"
    );
    summary.realizadoFatura.valor += getMetric(
      row,
      "realizadoFatura",
      "valor"
    );
    summary.aRealizarFatura.quantidadeCte += getMetric(
      row,
      "aRealizarFatura",
      "quantidadeCte"
    );
    summary.aRealizarFatura.valor += getMetric(
      row,
      "aRealizarFatura",
      "valor"
    );
  });

  return summary;
};

const SummaryMetricCard = ({ label, value, helper, iconClassName, tone }) => (
  <div className="faturamento-summary-card">
    <div className="faturamento-summary-card__header">
      <span>{label}</span>
      <div className={`faturamento-summary-card__icon faturamento-summary-card__icon--${tone}`}>
        <i className={iconClassName} />
      </div>
    </div>
    <div className="faturamento-summary-card__value">{value}</div>
    <div className="faturamento-summary-card__helper">{helper}</div>
  </div>
);

// PO1-3632: `onBaixarCard` abre o modal do card PNG; o botão só habilita com
// uma consulta que trouxe dados (`podeBaixarCard`).
const FaturamentoSummary = ({ summary, onBaixarCard, podeBaixarCard }) => {
  const nfsPrevistas = Number(summary.previstoNf.quantidade || 0);
  const nfsRealizadasCte = Number(summary.realizadoCte.quantidade || 0);
  const nfsPendentesCte = Math.max(0, nfsPrevistas - nfsRealizadasCte);

  const metrics = [
    {
      key: "previsto",
      label: "Previsto em NF",
      value: `${fmtInt(nfsPrevistas)} NF(s)`,
      helper: "no recorte",
      iconClassName: "tim-icons icon-notes",
      tone: "info",
    },
    {
      key: "realizado",
      label: "Realizado em CTe",
      value: `${fmtInt(nfsRealizadasCte)} NF(s)`,
      helper: `${fmtInt(summary.realizadoCte.quantidadeCte)} documento(s)`,
      iconClassName: "tim-icons icon-delivery-fast",
      tone: "success",
    },
    {
      key: "aRealizar",
      label: "A realizar em CTe",
      value: `${fmtInt(nfsPendentesCte)} NF(s)`,
      helper: "sem CTe",
      iconClassName: "tim-icons icon-time-alarm",
      tone: "warning",
    },
    {
      key: "faturas",
      label: "Realizado em fatura",
      value: fmtBRL(summary.realizadoFatura.valor),
      helper: `${fmtInt(summary.realizadoFatura.quantidadeCte)} documento(s), ${fmtInt(summary.realizadoFatura.quantidade)} fatura(s)`,
      iconClassName: "tim-icons icon-check-2",
      tone: "pink",
    },
    {
      key: "aRealizarFatura",
      label: "A realizar fatura",
      value: fmtBRL(summary.aRealizarFatura.valor),
      helper: `${fmtInt(summary.aRealizarFatura.quantidadeCte)} documento(s) sem fatura ativa`,
      iconClassName: "tim-icons icon-time-alarm",
      tone: "warning",
    },
  ];

  return (
    <Card className="card-everlog-shell faturamento-summary-overview">
      <CardHeader className="faturamento-card-header">
        <div>
          <div className="faturamento-card-eyebrow">Resumo do recorte</div>
          <CardTitle tag="h4" className="mb-1">
            Visão operacional e financeira
          </CardTitle>
          <small className="faturamento-muted-text">
            Totais agregados por transportadora para acompanhar NFs, CTes e
            faturas antes da abertura por transportadora, negócio e UF.
          </small>
        </div>
        {onBaixarCard ? (
          <Button
            type="button"
            size="sm"
            className="faturamento-button-primary faturamento-button-primary--sm faturamento-card-png-btn"
            onClick={onBaixarCard}
            disabled={!podeBaixarCard}
            title="Baixar card compartilhável (PNG) com o volume auditado do recorte"
          >
            <i className="tim-icons icon-image-02" /> Baixar card (PNG)
          </Button>
        ) : null}
      </CardHeader>
      <CardBody>
        <div className="faturamento-summary-grid">
          {metrics.map((metric) => (
            <SummaryMetricCard key={metric.key} {...metric} />
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

// Linha de filtros do card: "Transportadora: X · Região: Y · Negócio: Z".
const montarFiltrosLabel = (rotulos) => {
  if (!rotulos) return null;
  const partes = [];
  if (rotulos.transportadora) partes.push(`Transportadora: ${rotulos.transportadora}`);
  if (rotulos.regioes && rotulos.regioes.length) partes.push(`Região: ${rotulos.regioes.join(", ")}`);
  if (rotulos.negocios && rotulos.negocios.length) partes.push(`Negócio: ${rotulos.negocios.join(", ")}`);
  if (rotulos.estados && rotulos.estados.length) partes.push(`UF: ${rotulos.estados.join(", ")}`);
  return partes.length ? partes.join(" · ") : null;
};

const IndicadorFaturamento = () => {
  const { hasEmpresaSelection, empresa } = useCompanies();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consultado, setConsultado] = useState(false);
  const [ultimoPayload, setUltimoPayload] = useState(null);
  // PO1-3632: card PNG do volume auditado.
  const [rotulosFiltros, setRotulosFiltros] = useState(null);
  const [cardAberto, setCardAberto] = useState(false);
  const [resumoMensal, setResumoMensal] = useState(null);
  const [resumoMensalLoading, setResumoMensalLoading] = useState(false);
  const [resumoMensalErro, setResumoMensalErro] = useState(null);
  const resumoMensalChaveRef = useRef(null);

  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [documentContext, setDocumentContext] = useState(null);
  const documentContextKeyRef = useRef(null);
  const documentRequestRef = useRef(0);

  const tree = useMemo(() => buildTree(rows), [rows]);
  const summary = useMemo(() => buildSummary(rows), [rows]);
  const hasRows = tree.length > 0;

  const handleConsultar = useCallback(async (payload, rotulos) => {
    setLoading(true);
    setError(null);
    setUltimoPayload(payload);
    setRotulosFiltros(rotulos || null);
    setResumoMensal(null);
    setResumoMensalErro(null);
    resumoMensalChaveRef.current = null;
    setDocumentContext(null);
    setDocumentData(null);
    setDocumentError(null);
    setDocumentLoading(false);
    documentContextKeyRef.current = null;
    documentRequestRef.current += 1;
    try {
      const res = await api.get("/indicadores/faturamento", {
        params: payload,
        timeout: TIMEOUT_INDICADOR_MS,
      });
      setRows(Array.isArray(res.data) ? res.data : []);
      setConsultado(true);
    } catch (e) {
      setRows([]);
      setConsultado(true);
      setError(
        (e && e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "Erro ao carregar indicador"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const normalizeDocumentContext = useCallback(({ node, target, label, recorte }) => {
    if (!target) return null;
    const nextRecorte = { ...(recorte || {}) };
    let nextLabel = label || "";

    if (node) {
      Object.assign(nextRecorte, node.recorte || {});
      nextLabel = `${label} — ${node.label}`;
      if (
        node.recorte &&
        node.recorte.regiaoFreteId === null &&
        node.nivel === 3
      ) {
        nextRecorte.regiaoFreteSemMatch = 1;
      }
    }

    return {
      target,
      label: nextLabel,
      recorte: nextRecorte,
    };
  }, []);

  const getDocumentContextKey = useCallback((context) => {
    if (!context) return "";
    return JSON.stringify({
      target: context.target,
      recorte: context.recorte || {},
    });
  }, []);

  const handleDocumentContext = useCallback(
    async (rawContext) => {
      if (!ultimoPayload) return;
      const context = normalizeDocumentContext(rawContext || {});
      if (!context) return;

      const contextKey = getDocumentContextKey(context);
      setDocumentContext(context);
      if (documentContextKeyRef.current === contextKey) return;
      documentContextKeyRef.current = contextKey;

      const requestId = documentRequestRef.current + 1;
      documentRequestRef.current = requestId;
      setDocumentLoading(true);
      setDocumentError(null);
      setDocumentData(null);
      try {
        const params = { ...ultimoPayload, target: context.target };
        Object.entries(context.recorte || {}).forEach(([k, v]) => {
          if (v !== null && v !== undefined) params[k] = v;
        });
        const res = await api.get("/indicadores/faturamento/drilldown", {
          params,
          timeout: TIMEOUT_INDICADOR_MS,
        });
        if (documentRequestRef.current === requestId) {
          setDocumentData(res.data);
        }
      } catch (e) {
        if (documentRequestRef.current === requestId) {
          setDocumentError(
            (e && e.response && e.response.data && e.response.data.error) ||
              e.message ||
              "Erro ao carregar documentos"
          );
        }
      } finally {
        if (documentRequestRef.current === requestId) {
          setDocumentLoading(false);
        }
      }
    },
    [getDocumentContextKey, normalizeDocumentContext, ultimoPayload]
  );

  const handleExportDocuments = useCallback(() => {
    exportFaturamentoDocuments(documentData, documentContext);
  }, [documentContext, documentData]);

  // PO1-3632: o volume mês a mês só é buscado ao abrir o modal (uma vez por recorte).
  const abrirCard = useCallback(async () => {
    if (!ultimoPayload) return;
    setCardAberto(true);
    const chave = JSON.stringify(ultimoPayload);
    // Mesma chave: já carregado ou ainda em voo (reabrir o modal não busca de novo).
    if (resumoMensalChaveRef.current === chave && (resumoMensal || resumoMensalLoading)) return;
    resumoMensalChaveRef.current = chave;
    // Resposta de um recorte anterior (nova consulta durante o fetch) é descartada.
    const vigente = () => resumoMensalChaveRef.current === chave;
    setResumoMensalLoading(true);
    setResumoMensalErro(null);
    try {
      const res = await api.get("/indicadores/faturamento/resumo-mensal", {
        params: ultimoPayload,
        timeout: TIMEOUT_INDICADOR_MS,
      });
      if (!vigente()) return;
      setResumoMensal(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (!vigente()) return;
      resumoMensalChaveRef.current = null;
      setResumoMensalErro(
        (e && e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "Erro ao carregar o volume mês a mês"
      );
    } finally {
      if (vigente()) setResumoMensalLoading(false);
    }
  }, [ultimoPayload, resumoMensal, resumoMensalLoading]);

  const dadosCard = useMemo(() => {
    if (!ultimoPayload || !resumoMensal) return null;
    return {
      embarcador: (empresa && empresa.label) || "",
      de: ultimoPayload.dataExpedicaoDe,
      ate: ultimoPayload.dataExpedicaoAte,
      filtrosLabel: montarFiltrosLabel(rotulosFiltros),
      kpis: {
        ctes: summary.realizadoCte.quantidadeCte,
        faturas: summary.realizadoFatura.quantidade,
        frete: summary.realizadoFatura.valor,
      },
      meses: resumoMensal,
    };
  }, [ultimoPayload, resumoMensal, empresa, rotulosFiltros, summary]);

  if (!hasEmpresaSelection) {
    return (
      <div className="content faturamento-page">
        <SpecificEmpresaRequiredNotice
          title="Selecione um embarcador no cabeçalho"
          message="Esta visão usa o embarcador ativo para montar o recorte de faturamento."
        />
      </div>
    );
  }

  return (
    <div className="content faturamento-page">
      <Card className="card-everlog-shell card-everlog-hero faturamento-hero">
        <CardBody>
          <div className="faturamento-hero__top">
            <div className="faturamento-hero__intro">
              <span className="faturamento-hero__eyebrow">
                Indicador de faturamento
              </span>
              <h2 className="faturamento-hero__title">
                Faturamento operacional e financeiro
              </h2>
              <p className="faturamento-hero__description">
                Acompanhe NFs previstas, realização em CTe e cobertura de
                faturas com mapa, ranking, esteira e heatmap.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="card-everlog-shell faturamento-filter-card">
        <CardHeader className="faturamento-card-header faturamento-filter-card__header">
          <div>
            <CardTitle tag="h4" className="mb-1">
              Recorte da análise
            </CardTitle>
            <small className="faturamento-muted-text">
              Use as datas como filtro principal. Modal, serviço, negócio, UF
              e região ficam no painel de filtros.
            </small>
          </div>
        </CardHeader>
        <CardBody>
          <FaturamentoFiltros onSubmit={handleConsultar} loading={loading} />
        </CardBody>
      </Card>

      {!loading && !error && consultado && hasRows && (
        <FaturamentoSummary
          summary={summary}
          onBaixarCard={abrirCard}
          podeBaixarCard={Boolean(ultimoPayload) && hasRows && !loading}
        />
      )}

      <CardPngModal
        isOpen={cardAberto}
        onClose={() => setCardAberto(false)}
        dados={dadosCard}
        carregandoDados={resumoMensalLoading}
        erroDados={resumoMensalErro}
      />

      {!loading && !error && consultado && hasRows && (
        <FaturamentoVisualExperiments
          tree={tree}
          onDrilldown={handleDocumentContext}
          onDocumentContextChange={handleDocumentContext}
        />
      )}

      <Card className="card-everlog-shell faturamento-results-card">
        <CardHeader className="faturamento-card-header faturamento-results-card__header">
          <div>
            <div className="faturamento-card-eyebrow">
              Documentos do faturamento
            </div>
            <CardTitle tag="h4" className="mb-1">
              Abertura do faturamento
            </CardTitle>
            <small className="faturamento-muted-text">
              Lista de documentos atualizada conforme a seleção da visão
              superior.
            </small>
          </div>
          <div className="faturamento-results-card__actions">
            <Button
              className="faturamento-button-secondary faturamento-button-secondary--sm faturamento-button-excel"
              onClick={handleExportDocuments}
              disabled={
                loading ||
                documentLoading ||
                !documentData ||
                !documentData.itens ||
                documentData.itens.length === 0
              }
            >
              <i className="tim-icons icon-cloud-download-93" />
              Exportar Excel
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {loading && (
            <div className="faturamento-state-panel">
              <ClipLoader size={42} color="#1d8cf8" loading />
              <h4>Carregando indicador</h4>
              <p>
                Buscando notas fiscais, CTes e faturas para consolidar a visão
                financeira do período.
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="faturamento-state-panel faturamento-state-panel--danger">
              <div className="faturamento-state-panel__eyebrow">Erro</div>
              <h4>Não foi possível carregar o indicador</h4>
              <p>{typeof error === "object" ? JSON.stringify(error) : error}</p>
            </div>
          )}

          {!loading && !error && !consultado && (
            <div className="faturamento-state-panel">
              <div className="faturamento-state-panel__eyebrow">
                Aguardando consulta
              </div>
              <h4>Defina o recorte para visualizar o faturamento</h4>
              <p>
                Escolha o período e, se necessário, refine os demais filtros
                antes de atualizar o indicador.
              </p>
            </div>
          )}

          {!loading && !error && consultado && (
            <FaturamentoDocumentList
              loading={documentLoading}
              error={documentError}
              data={documentData}
              context={documentContext}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default IndicadorFaturamento;
